import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, X, Mail } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

function currentRigPeriod() {
  const now = new Date();
  const half = now.getMonth() < 6 ? 'H1' : 'H2';
  return `${now.getFullYear()}-${half}`;
}

// Records a free postal entry into the "Win a Sim Rig" draw. Postal entries are
// competition_type 'sim_rig_paid' with is_paid=false and source 'postal', so
// they pool into the same random draw as paid £5 entries on equal terms
// (drawSimRigWinner filters by competition_type + rig_draw_period only).
export default function AddPostalEntryModal({ onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    address_line1: '', address_city: '', postcode: '', date_of_birth: '',
    rig_draw_period: currentRigPeriod(), marketing_opt_in: false, notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError('');
    if (!form.first_name.trim() || !form.last_name.trim()) { setError('First and last name are required.'); return; }
    if (!form.address_line1.trim() || !form.address_city.trim() || !form.postcode.trim()) { setError('Full postal address is required (we contact postal winners by post).'); return; }
    if (!form.date_of_birth) { setError('Date of birth is required (18+ only).'); return; }
    const age = (Date.now() - new Date(form.date_of_birth).getTime()) / (365.25 * 24 * 3600 * 1000);
    if (age < 18) { setError('Postal entrants must be 18 or over for the sim rig draw.'); return; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Email address looks invalid.'); return; }

    setSaving(true);
    try {
      await base44.entities.CompetitionEntry.create({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: (form.email || '').trim().toLowerCase(),
        phone: (form.phone || '').trim(),
        postcode: (form.postcode || '').trim(),
        date_of_birth: form.date_of_birth,
        address_line1: (form.address_line1 || '').trim(),
        address_city: (form.address_city || '').trim(),
        experience_preference: 'any',
        marketing_opt_in: !!form.marketing_opt_in,
        source: 'postal',
        competition_type: 'sim_rig_paid',
        is_paid: false,
        paid_amount: 0,
        rig_draw_period: form.rig_draw_period,
        square_payment_id: 'POSTAL-' + Date.now(),
        notes: form.notes || '',
      });
      toast({ title: 'Postal entry recorded', description: `${form.first_name} ${form.last_name} → ${form.rig_draw_period}` });
      onCreated?.();
    } catch (e) {
      setError(e?.message || 'Failed to record entry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111115] border border-green-500/30 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white" aria-label="Close"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-4 h-4 text-green-400" />
          <p className="text-green-400 text-[10px] font-bold tracking-widest">FREE POSTAL ENTRY · SIM RIG DRAW</p>
        </div>
        <h3 className="text-white font-black text-xl uppercase mb-1">Record a Postal Entry</h3>
        <p className="text-white/40 text-xs mb-5 leading-relaxed">Enter details exactly as received on the postal entry form. This creates a free entry pooled with paid entries on equal terms.</p>

        {error && <div className="mb-4 px-4 py-3 bg-red-500/5 border border-red-500/30 text-red-300 text-xs">{error}</div>}

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="FIRST NAME *"><input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className="seo-input" /></Field>
          <Field label="LAST NAME *"><input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className="seo-input" /></Field>
          <Field label="EMAIL (optional)" full><input value={form.email} onChange={(e) => set('email', e.target.value)} className="seo-input" /></Field>
          <Field label="PHONE (optional)"><input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="seo-input" /></Field>
          <Field label="DATE OF BIRTH *"><input type="date" value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} className="seo-input" /></Field>
          <Field label="ADDRESS LINE 1 *" full><input value={form.address_line1} onChange={(e) => set('address_line1', e.target.value)} className="seo-input" /></Field>
          <Field label="CITY / TOWN *"><input value={form.address_city} onChange={(e) => set('address_city', e.target.value)} className="seo-input" /></Field>
          <Field label="POSTCODE *"><input value={form.postcode} onChange={(e) => set('postcode', e.target.value)} className="seo-input" /></Field>
          <Field label="DRAW PERIOD" full>
            <input value={form.rig_draw_period} onChange={(e) => set('rig_draw_period', e.target.value)} className="seo-input" placeholder="2026-H2" />
          </Field>
          <div className="sm:col-span-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => set('marketing_opt_in', e.target.checked)} className="mt-1 accent-[#e11d2a]" />
              <span className="text-white/40 text-xs leading-relaxed">Entrant opted in to marketing emails.</span>
            </label>
          </div>
          <Field label="NOTES (optional)" full><textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} className="seo-input" rows={2} /></Field>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={submit} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold text-xs tracking-widest px-6 py-3.5 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> SAVING…</> : 'RECORD POSTAL ENTRY'}
          </button>
          <button onClick={onClose} className="px-5 border border-white/10 text-white/50 hover:text-white text-xs font-bold tracking-widest">CANCEL</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}